import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { UserService } from "./user.service";
import { AccountService } from "@modules/account/services/account.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { StockTestService } from "@main-services/stock-test.service";
import { setReferralCookies } from "@modules/social-trading/helpers/cookies";
import { take } from "rxjs/operators";

@Injectable({
	providedIn: "root"
})
export class ShareTradeGuard {
	constructor(
		private router: Router,
		private fiatWalletService: FiatWalletService,
		private accountService: AccountService,
		private userService: UserService,
		private stockTestService: StockTestService,
		private stockTestHelperService: StockTestHelperService
	) {}

	public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
		const { refId, assetId } = route.params;
		setReferralCookies(refId);
		this.userService
			.getSession()
			.pipe(take(1))
			.subscribe((isSessionValid: boolean) => {
				if (isSessionValid) {
					this.fiatWalletService
						.initWallets(true)
						.pipe(take(1))
						.subscribe(() => {
							if (this.userService.isUnverified) {
								this.router.navigateByUrl("home/trade");
							} else {
								this.applyShareTrade(assetId);
							}
						});
				} else {
					this.redirectToLogin(state.url);
				}
			});
		return false;
	}

	private applyShareTrade(assetId: string): void {
		if (this.walletHasMinimumRequiredAmount()) {
			this.showAppropriatenessQuestionnaireIfNeeded(assetId).then(() => {
				this.router.navigateByUrl(`trade/buy/${assetId}`);
			});
		} else {
			this.router.navigateByUrl("home/deposit");
		}
	}

	private showAppropriatenessQuestionnaireIfNeeded(assetId: string): Promise<void> {
		return new Promise((resolve) => {
			if (this.stockTestService.needsTest(assetId)) {
				this.router.navigateByUrl("home").then(() => {
					this.stockTestHelperService.startTest(resolve);
				});
			} else {
				resolve(null);
			}
		});
	}

	private redirectToLogin(inviteURL: string): void {
		this.userService.redirect = inviteURL;
		this.router.navigateByUrl("/user/login");
	}

	private walletHasMinimumRequiredAmount(): boolean {
		const defaultFiat = this.accountService.getDefaultFiat();
		const defaultWallet = this.fiatWalletService.getWalletByFiatId(defaultFiat.getId());
		const walletMinDeposit = defaultFiat.getWalletMinDeposit();
		const walletBalance = defaultWallet.getBalance();
		return walletBalance >= walletMinDeposit;
	}
}
